import React, { useState, useEffect } from 'react';
import { BetItem } from './BettingSlipDrawer';
import { getPreEvents, getLiveEvents } from '../api/api';
import { EventSnapshot, Market, Outcome } from '../api/types';
import MatchDetailPage from './MatchDetailPage';

interface SportMatchesPageProps {
  sportLabel: string;
  sportEmoji: string;
  sportId: string;
  timeFilter: string;
  onBack: () => void;
  onLoginClick: () => void;
  bets: BetItem[];
  onAddBet: (bet: BetItem) => void;
  onRemoveBet: (id: string) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function countryCodeToFlag(code?: string): string {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  return String.fromCodePoint(upper.charCodeAt(0) + 127397, upper.charCodeAt(1) + 127397);
}

const KNOWN_FLAGS: Record<string, string> = {
  International: '🌍', 'Champions League': '🏆', 'Europa League': '🏆',
  'UEFA Champions League': '🏆', 'UEFA Europa League': '🏆',
  UEFA: '🏆', 'World Cup': '🌍', 'Copa America': '🌎',
  'Africa Cup': '🌍', 'Club World Cup': '🏆',
};

function categoryFlag(name: string, cc?: string): string {
  if (KNOWN_FLAGS[name]) return KNOWN_FLAGS[name];
  return countryCodeToFlag(cc) || '🏴';
}

const TOP_CATEGORY_PRIORITY = [
  'Champions League','UEFA Champions League','Europa League','UEFA Europa League',
  'International','England','Germany','Spain','France','Italy',
  'Portugal','Netherlands','Brazil','Turkey','Belgium','Scotland',
  'Argentina','USA','Saudi Arabia','Norway',
];

function formatOdds(val: string | number | undefined): string {
  if (!val) return '-';
  const n = parseFloat(String(val));
  if (isNaN(n) || n <= 1) return '-';
  return n.toFixed(2).replace('.', ',');
}

function find1x2(markets: Market[]): Market | undefined {
  return markets.find((m) => m.market_id === '1' && m.status !== -1);
}

function getOutcome(market: Market, id: string): Outcome | undefined {
  return market.outcomes.find((o) => o.outcome_id === id && o.active);
}

function formatEventTime(ev: EventSnapshot): string {
  const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
  if (!starts) return '';
  return new Date(starts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function isWithinHours(ev: EventSnapshot, hours: number): boolean {
  const starts = ev.event?.scheduled_at ?? ev.event?.start_time;
  if (!starts) return false;
  const d = new Date(starts).getTime();
  const now = Date.now();
  return d >= now && d <= now + hours * 3600_000;
}

// ── Data models ────────────────────────────────────────────────────────────

interface CategoryEntry { name: string; flag: string; count: number; }
interface TournamentEntry { name: string; flag: string; count: number; }
interface MatchEntry {
  id: string; time: string; team1: string; team2: string;
  odds: { home: string; draw: string; away: string };
  marketId: string; moreCount: number;
}
interface LeagueGroup { name: string; matches: MatchEntry[]; }

function buildCategories(events: EventSnapshot[]): CategoryEntry[] {
  const map = new Map<string, { count: number; cc?: string }>();
  for (const ev of events) {
    const cat = ev.event?.category_name ?? 'Other';
    const cc = ev.event?.country_code;
    const prev = map.get(cat) ?? { count: 0, cc };
    map.set(cat, { count: prev.count + 1, cc: prev.cc ?? cc });
  }
  return Array.from(map.entries()).map(([name, val]) => ({
    name, flag: categoryFlag(name, val.cc), count: val.count,
  }));
}

function buildTournaments(events: EventSnapshot[], catFlag: string): TournamentEntry[] {
  const map = new Map<string, number>();
  for (const ev of events) {
    const t = ev.event?.tournament_name ?? 'Other';
    map.set(t, (map.get(t) ?? 0) + 1);
  }
  return Array.from(map.entries()).map(([name, count]) => ({ name, flag: catFlag, count }));
}

function buildLeagueGroups(events: EventSnapshot[]): LeagueGroup[] {
  const map = new Map<string, MatchEntry[]>();
  for (const ev of events) {
    const market = find1x2(ev.markets ?? []);
    const homeO = market ? getOutcome(market, '1') : undefined;
    const drawO = market ? getOutcome(market, '2') : undefined;
    const awayO = market ? getOutcome(market, '3') : undefined;
    const entry: MatchEntry = {
      id: ev.event_id,
      time: formatEventTime(ev),
      team1: ev.event?.home?.name ?? 'Home',
      team2: ev.event?.away?.name ?? 'Away',
      odds: {
        home: formatOdds(homeO?.odds),
        draw: formatOdds(drawO?.odds),
        away: formatOdds(awayO?.odds),
      },
      marketId: market?.market_id ?? '1',
      moreCount: (ev.markets ?? []).length,
    };
    const tourney = ev.event?.tournament_name ?? 'Other';
    if (!map.has(tourney)) map.set(tourney, []);
    map.get(tourney)!.push(entry);
  }
  return Array.from(map.entries()).map(([name, matches]) => ({ name, matches }));
}

// ── Constants ──────────────────────────────────────────────────────────────

const TIME_FILTERS = [
  { id: 'live',  label: 'Live',    hours: 0,  image: '/images/live_time.png' },
  { id: 'heute', label: 'Heute',   hours: 24, image: '/images/haute.png' },
  { id: '3std',  label: '3 Std.',  hours: 3,  image: '/images/3-hours.png' },
  { id: '48std', label: '48 Std.', hours: 48, image: '/images/48-hours.png' },
];

const BET_FILTERS = ['Tipp', 'Über/Unter', 'Handicap', 'Beide Teams treffen'];

// ── Component ──────────────────────────────────────────────────────────────

const SportMatchesPage: React.FC<SportMatchesPageProps> = ({
  sportLabel, sportId, onBack, onLoginClick, bets, onAddBet, onRemoveBet,
}) => {
  const [activeFilter, setActiveFilter] = useState('heute');
  const [allPreEvents, setAllPreEvents] = useState<EventSnapshot[]>([]);
  const [liveEvents, setLiveEvents] = useState<EventSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  // navigation stack: null → category list → tournament list → match list → match detail
  const [selectedCategory, setSelectedCategory] = useState<CategoryEntry | null>(null);
  const [selectedTournament, setSelectedTournament] = useState<string | null>(null); // null = Alle Spiele
  const [showMatches, setShowMatches] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventSnapshot | null>(null);
  const [activeBetFilter, setActiveBetFilter] = useState('Tipp');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getPreEvents({ sport_id: sportId, hours: 48 }).catch(() => ({ items: [] as EventSnapshot[] })),
      getLiveEvents().catch(() => ({ items: [] as EventSnapshot[] })),
    ]).then(([pre, live]) => {
      setAllPreEvents(pre.items);
      setLiveEvents(live.items.filter((e) => e.event?.sport_id === sportId));
    }).finally(() => setLoading(false));
  }, [sportId]);

  // Active events for current filter
  const activeEvents: EventSnapshot[] = (() => {
    if (activeFilter === 'live') return liveEvents;
    const hours = TIME_FILTERS.find((f) => f.id === activeFilter)?.hours ?? 24;
    return allPreEvents.filter((e) => isWithinHours(e, hours));
  })();

  // Counts per filter
  const counts: Record<string, number> = {
    live: liveEvents.length,
    heute: allPreEvents.filter((e) => isWithinHours(e, 24)).length,
    '3std': allPreEvents.filter((e) => isWithinHours(e, 3)).length,
    '48std': allPreEvents.length,
  };

  // Visible time filter buttons (only show those with events OR currently active)
  const visibleFilters = TIME_FILTERS.filter((f) => counts[f.id] > 0 || f.id === activeFilter);

  // Category list
  const categories = buildCategories(activeEvents);
  const topCategories = categories
    .filter((c) => TOP_CATEGORY_PRIORITY.includes(c.name))
    .sort((a, b) => TOP_CATEGORY_PRIORITY.indexOf(a.name) - TOP_CATEGORY_PRIORITY.indexOf(b.name));
  const azCategories = categories
    .filter((c) => !TOP_CATEGORY_PRIORITY.includes(c.name))
    .sort((a, b) => a.name.localeCompare(b.name));

  // Tournament list for selected category
  const categoryEvents = selectedCategory
    ? activeEvents.filter((e) => e.event?.category_name === selectedCategory.name)
    : [];
  const tournaments = buildTournaments(categoryEvents, selectedCategory?.flag ?? '⚽');

  // Match list events (filtered by tournament if one is selected)
  const matchEvents = showMatches
    ? (selectedTournament
        ? categoryEvents.filter((e) => e.event?.tournament_name === selectedTournament)
        : categoryEvents)
    : [];
  const leagues = buildLeagueGroups(matchEvents);

  // ── Odds handlers ────────────────────────────────────────────────────────

  const isOddsSelected = (eventId: string, type: 'home' | 'draw' | 'away') =>
    bets.some((b) => b.id === `pre-${eventId}-${type}`);

  const handleOddsClick = (match: MatchEntry, type: 'home' | 'draw' | 'away') => {
    const id = `pre-${match.id}-${type}`;
    if (isOddsSelected(match.id, type)) {
      onRemoveBet(id);
    } else {
      const existing = bets.find((b) => b.matchId === match.id);
      if (existing) onRemoveBet(existing.id);
      const tippLabels = { home: 'Heimsieg', draw: 'Unentschieden', away: 'Auswärtssieg' };
      onAddBet({
        id,
        matchId: match.id,
        matchName: `${match.team1} - ${match.team2}`,
        betType: type,
        tippLabel: tippLabels[type],
        odds: match.odds[type],
        eventId: match.id,
        marketId: match.marketId,
        outcomeId: type === 'home' ? '1' : type === 'draw' ? '2' : '3',
      });
    }
  };

  // ── Sub-renders ──────────────────────────────────────────────────────────

  const renderHeader = (title: string, backFn: () => void) => (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-100 sticky top-0 z-30">
      <button onClick={backFn} className="text-gray-800 p-1">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      <span className="text-base font-bold text-gray-900">{title}</span>
      <button onClick={onLoginClick} className="text-sm font-semibold text-gray-700 hover:text-red-600 transition">
        Anmelden
      </button>
    </div>
  );

  const renderTimeTabs = (onFilterChange?: () => void) => (
    <div className="bg-white border-b border-gray-200 px-4 py-3 flex gap-4 w-full">
      {visibleFilters.map((f) => {
        const active = activeFilter === f.id;
        const count = counts[f.id];
        return (
          <button
            key={f.id}
            onClick={() => {
              setActiveFilter(f.id);
              setSelectedTournament(null);
              setShowMatches(false);
              if (onFilterChange) onFilterChange();
            }}
            className={`flex flex-col items-center w-1/4 pb-2 border-b-2 transition ${
              active ? 'border-red-600' : 'border-transparent'
            }`}
          >
            <div className="relative mb-1">
              <img src={f.image} alt={f.label} className="w-10 h-10 object-contain" />
              {!loading && count > 0 && (
                <span className="absolute -top-1.5 -right-3.5 text-xs font-bold text-gray-500">
                  {count}
                </span>
              )}
            </div>
            <span className={`text-xs font-semibold ${
              active ? 'text-gray-900' : 'text-gray-500'
            }`}>{f.label}</span>
          </button>
        );
      })}
    </div>
  );

  const renderCategoryRow = (cat: CategoryEntry, idx: number, arr: CategoryEntry[]) => (
    <button
      key={cat.name}
      onClick={() => setSelectedCategory(cat)}
      className={`w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 active:bg-gray-100 transition ${
        idx < arr.length - 1 ? 'border-b border-gray-100' : ''
      }`}
    >
      <span className="text-2xl w-8 text-center flex-shrink-0 leading-none">{cat.flag}</span>
      <span className="flex-1 text-[15px] font-medium text-gray-900">{cat.name}</span>
      <span className="text-[15px] font-medium text-gray-600">{cat.count}</span>
    </button>
  );

  // ── VIEW 4: Match detail ─────────────────────────────────────────────────

  if (selectedEvent) {
    return (
      <MatchDetailPage
        event={selectedEvent}
        onBack={() => setSelectedEvent(null)}
        bets={bets}
        onAddBet={onAddBet}
        onRemoveBet={onRemoveBet}
        onLoginClick={onLoginClick}
      />
    );
  }

  // ── VIEW 3: Match list ───────────────────────────────────────────────────

  if (showMatches && selectedCategory) {
    const title = selectedTournament
      ? `${sportLabel} ${selectedCategory.name}`
      : `${sportLabel} ${selectedCategory.name}`;
    return (
      <div className="min-h-screen bg-gray-100 pb-20">
        {renderHeader(title, () => { setShowMatches(false); setSelectedTournament(null); })}
        {renderTimeTabs(() => setSelectedCategory(null))}
        <div className="px-3 pt-3">
          <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
            {BET_FILTERS.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveBetFilter(filter)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition ${
                  activeBetFilter === filter
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
          {leagues.length === 0 && (
            <p className="text-center text-gray-500 text-sm mt-10">Keine Ereignisse verfügbar.</p>
          )}
          <div className="flex flex-col gap-3">
            {leagues.map((league) => (
              <div key={league.name} className="bg-white rounded-xl overflow-hidden shadow-sm">
                <div className="px-4 py-3 border-b border-gray-100">
                  <span className="text-sm font-bold text-red-600">{league.name}</span>
                </div>
                {league.matches.map((match, idx) => (
                  <div key={match.id} className={idx > 0 ? 'border-t border-gray-100' : ''}>
                    <div
                      className="px-4 pt-3 pb-1 flex gap-3 items-center cursor-pointer active:bg-gray-50"
                      onClick={() => {
                        const ev = matchEvents.find((e) => e.event_id === match.id);
                        if (ev) setSelectedEvent(ev);
                      }}
                    >
                      <span className="text-xs font-bold text-gray-500 min-w-[36px]">{match.time}</span>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-800 mb-1">{match.team1}</div>
                        <div className="text-sm font-medium text-gray-800">{match.team2}</div>
                      </div>
                    </div>
                    <div className="flex gap-2 px-4 pb-3 items-center">
                      {(['home', 'draw', 'away'] as const).map((type, i) => {
                        const labels = ['1', 'X', '2'];
                        const oddsVal = match.odds[type];
                        const sel = isOddsSelected(match.id, type);
                        return (
                          <button
                            key={type}
                            onClick={() => handleOddsClick(match, type)}
                            disabled={oddsVal === '-'}
                            className={`flex-1 flex items-center justify-between rounded-lg px-3 py-2 transition ${
                              sel ? 'bg-gray-800' : oddsVal === '-' ? 'bg-gray-50 opacity-40' : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                          >
                            <span className={`text-xs ${sel ? 'text-gray-300' : 'text-gray-500'}`}>{labels[i]}</span>
                            <span className={`text-sm font-bold ${sel ? 'text-white' : 'text-gray-800'}`}>{oddsVal}</span>
                          </button>
                        );
                      })}
                      <button
                            className="text-xs text-gray-500 whitespace-nowrap ml-1 flex-shrink-0"
                            onClick={() => {
                              const ev = matchEvents.find((e) => e.event_id === match.id);
                              if (ev) setSelectedEvent(ev);
                            }}
                          >
                            {match.moreCount} ›
                          </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── VIEW 2: Tournament list ──────────────────────────────────────────────

  if (selectedCategory) {
    const totalCount = categoryEvents.length;
    return (
      <div className="min-h-screen bg-gray-100 pb-20">
        {renderHeader(`${sportLabel} ${selectedCategory.name}`, () => setSelectedCategory(null))}
        {renderTimeTabs(() => setSelectedCategory(null))}

        {loading && (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent" />
          </div>
        )}

        {!loading && (
          <div className="mt-3 bg-white">
            {/* Alle Spiele row */}
            <button
              onClick={() => { setSelectedTournament(null); setShowMatches(true); }}
              className="w-full flex items-center gap-4 px-4 py-4 text-left active:bg-gray-50 transition border-b border-gray-100"
            >
              <span className="text-2xl w-9 text-center flex-shrink-0 leading-none">{selectedCategory.flag}</span>
              <span className="flex-1 text-[15px] font-bold text-gray-900">Alle Spiele</span>
              <span className="text-[15px] font-medium text-gray-600">{totalCount}</span>
            </button>

            {/* Section label */}
            {tournaments.length > 0 && (
              <div className="px-4 pt-4 pb-2">
                <span className="text-[13px] font-bold text-gray-900">Spiel</span>
              </div>
            )}

            {/* Tournament rows */}
            {tournaments.map((t, idx, arr) => (
              <button
                key={t.name}
                onClick={() => { setSelectedTournament(t.name); setShowMatches(true); }}
                className={`w-full flex items-center gap-4 px-4 py-4 text-left active:bg-gray-50 transition ${
                  idx < arr.length - 1 ? 'border-b border-gray-100' : ''
                }`}
              >
                <span className="text-2xl w-9 text-center flex-shrink-0 leading-none">{t.flag}</span>
                <span className="flex-1 text-[15px] font-medium text-gray-900 truncate pr-2">{t.name}</span>
                <span className="text-[15px] font-medium text-gray-500 flex-shrink-0">{t.count}</span>
              </button>
            ))}
          </div>
        )}

        {!loading && categoryEvents.length === 0 && (
          <p className="text-center text-gray-500 text-sm mt-10">Keine Ereignisse verfügbar.</p>
        )}
      </div>
    );
  }

  // ── VIEW 1: Category list ────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {renderHeader(sportLabel, onBack)}
      {renderTimeTabs()}
      <div className="px-3 pt-4 flex flex-col gap-4">
        {loading && (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent" />
          </div>
        )}
        {!loading && categories.length === 0 && (
          <p className="text-center text-gray-500 text-sm mt-10">Keine Ereignisse verfügbar.</p>
        )}
        {!loading && topCategories.length > 0 && (
          <div>
            <h2 className="text-base font-black text-gray-900 mb-2 px-1">Top</h2>
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              {topCategories.map((cat, idx, arr) => renderCategoryRow(cat, idx, arr))}
            </div>
          </div>
        )}
        {!loading && azCategories.length > 0 && (
          <div>
            <h2 className="text-base font-black text-gray-900 mb-2 px-1">A-Z</h2>
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
              {azCategories.map((cat, idx, arr) => renderCategoryRow(cat, idx, arr))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SportMatchesPage;
